/**
 * Utility functions for number validation and NaN checking
 */

/**
 * Checks if a value is a valid number (not NaN, not null, not undefined)
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

/**
 * Safely parses a string or number to a valid number, returning null if invalid
 * Handles formatted numbers (with commas, spaces, currency symbols, etc.)
 */
export function safeParseNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  
  if (typeof value === 'number') {
    return isValidNumber(value) ? value : null;
  }
  
  if (typeof value === 'string') {
    // Remove common formatting: commas, spaces, currency symbols, etc.
    const cleaned = value
      .replace(/[,\s€$£¥]/g, '') // Remove commas, spaces, currency symbols
      .replace(/[^\d.-]/g, ''); // Keep only digits, dots, and minus signs
    
    // Handle empty string after cleaning
    if (cleaned === '' || cleaned === '-') {
      return null;
    }
    
    const parsed = parseFloat(cleaned);
    return isValidNumber(parsed) ? parsed : null;
  }
  
  return null;
}

/**
 * Checks if a number is positive (greater than 0)
 */
export function isPositiveNumber(value: unknown): boolean {
  const num = safeParseNumber(value);
  return num !== null && num > 0;
}

/**
 * Checks if at least one of the provided values is a valid number
 */
export function hasAnyValidNumber(...values: unknown[]): boolean {
  return values.some(value => safeParseNumber(value) !== null);
}

/**
 * Filters out invalid numbers from an array
 */
export function filterValidNumbers(values: unknown[]): number[] {
  return values.map(safeParseNumber).filter((num): num is number => num !== null);
}

/**
 * Safely gets a number value or returns null if invalid
 */
export function getValidNumberOrNull(value: unknown): number | null {
  return safeParseNumber(value);
}

